import { useContext, useMemo } from 'react'

import { Text } from '@hhgtech/hhg-components/mantine'
import { Hospital, Stethoscope } from '@hhgtech/icons/other'
import { Box, BoxProps, Flex } from '@mantine/core'

import Expert from 'components/atoms/icons/Expert'
import { NextImageWrap } from 'components/atoms/imageWrap'
import { CarePathDefaultContext } from 'components/templates/carePath/landing/context'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath, DEFAULT_IMG } from 'interfaces/constants'
import { ContentExpertCard } from 'interfaces/types'

import useStyles from './index.styles'

export type Props = ContentExpertCard & {
  size?: 'lg' | 'md'
} & BoxProps

export const ExpertCard = ({
  name,
  position,
  specialty,
  organisation,
  url,
  avatar,
  role,
  size: sizeInit = 'md',
  styles,
  className,
  classNames,
  ...rest
}: Props) => {
  const { activeTheme } = useContext(CarePathDefaultContext)
  const { locale } = useRouterExtend()
  const isMobile = useIsMobile()
  const size = useMemo(() => (isMobile ? 'md' : sizeInit), [isMobile])
  const iconSize = useMemo(() => (size === 'lg' ? 22 : 18), [size])
  const { classes, cx, theme } = useStyles(
    { size, color: activeTheme?.primary },
    {
      name: 'ExpertCard',
      styles,
      classNames,
    },
  )
  const commonTracking = useMemo(
    () => ({
      'data-event-category': 'Service Connection',
      'data-event-action': 'See Doctor Profile',
      'data-event-label': `https://${domainLocales[locale]}${url}`,
    }),
    [locale, url],
  )

  return (
    <Box
      component="a"
      href={url}
      title={name}
      target="_blank"
      className={cx(className, classes.root)}
      {...commonTracking}
      {...rest}
    >
      <Box className={classes.thumbCol}>
        <Box className={classes.thumb}>
          <NextImageWrap
            loading="lazy"
            src={avatar || DEFAULT_IMG}
            alt={name}
            width={size === 'lg' ? 160 : isMobile ? 48 : 64}
            height={size === 'lg' ? 160 : isMobile ? 48 : 64}
          />
          <Box className={classes.expertIcon}>
            {role === 'medical' ? (
              <Expert
                size={isMobile ? 16 : size === 'lg' ? 40 : 24}
                color={theme.colors.red[5]}
              />
            ) : (
              <img
                src={`${assetImagePath}/article-${role}.svg`}
                alt=""
                loading="lazy"
              />
            )}
          </Box>
        </Box>
        <Box className={classes.nameAreaSmSize}>
          <Text
            size={isMobile ? 's3' : size === 'lg' ? 's2' : 's3'}
            className={classes.name}
          >
            {name}
          </Text>
        </Box>
      </Box>
      <Box className={classes.info}>
        <Box className={classes.nameAreaLgSize}>
          <Text
            size={isMobile ? 's3' : size === 'lg' ? 's2' : 's3'}
            className={classes.name}
          >
            {name}
          </Text>
        </Box>
        {(specialty || organisation) && (
          <Box className={classes.hospitalInfo}>
            {specialty && (
              <Flex align={'flex-start'} gap={size === 'lg' ? 8 : 4}>
                <Box>
                  <Stethoscope size={iconSize} />
                </Box>
                <Text size="p4">{specialty}</Text>
              </Flex>
            )}
            {organisation && (
              <Flex align={'flex-start'} gap={size === 'lg' ? 8 : 4}>
                <Box>
                  <Hospital size={iconSize} />
                </Box>
                <Text size="p4">{position || organisation}</Text>
              </Flex>
            )}
          </Box>
        )}

        {/* {url && (
          <Box className={classes.cta}>
            <Text size="s4" as="span" color={activeTheme?.primary}>
              {f({ id: 'contentExpert.detailCta' })}
            </Text>
            <ArrowRight size={18} color={activeTheme?.primary} />
          </Box>
        )} */}
      </Box>
    </Box>
  )
}
